import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  Grid,
  Card,
  CardContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Divider,
  MenuItem,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Add, Remove, Delete } from "@mui/icons-material";
import { addNewRestaurantInvoice } from "../../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../../../theme";
import { useNavigate } from "react-router-dom";
import {
  listAllMenuItems,
  resetAllMenuItems,
} from "../../../store/actions/menuItemActions";

const TAX_RATE = 0.025;
const INITIAL_ITEMS_TO_SHOW = 8;
const formatPrice = (amount) => `₹${Number(amount || 0).toFixed(2)}`;

/* ---------------- MENU GRID ---------------- */
const MenuGrid = ({
  items = [],
  isLoading,
  onAddItem,
  onShowMore,
  showAll,
  colors,
}) => {
  const displayed = showAll ? items : items.slice(0, INITIAL_ITEMS_TO_SHOW);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (!items.length) {
    return <Alert severity="info">No menu items found</Alert>;
  }

  return (
    <>
      <Grid container spacing={2}>
        {displayed.map((item) => (
          <Grid item xs={12} sm={6} md={3} key={item._id}>
            <Card
              sx={{
                cursor: "pointer",
                bgcolor: colors.primary[400],
                "&:hover": { boxShadow: 6 },
              }}
              onClick={() => onAddItem(item)}
            >
              <CardContent>
                <Typography fontWeight={600} noWrap>
                  {item.item_name}
                </Typography>
                <Typography variant="body2">{item.category}</Typography>
                <Typography variant="h6" mt={1}>
                  {formatPrice(item.price)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {items.length > INITIAL_ITEMS_TO_SHOW && !showAll && (
        <Box textAlign="center" mt={2}>
          <Button variant="outlined" onClick={onShowMore}>
            View More
          </Button>
        </Box>
      )}
    </>
  );
};

/* ---------------- ORDER SUMMARY ---------------- */
const OrderSummary = ({ items, onQuantityChange, onRemove }) => {
  if (!items.length) return null;

  return (
    <Box mt={4}>
      <Typography variant="h6">Order Summary</Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Item</TableCell>
            <TableCell align="center">Qty</TableCell>
            <TableCell align="right">Rate</TableCell>
            <TableCell align="right">Amount</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => (
            <TableRow key={item._id}>
              <TableCell>{item.item_name}</TableCell>
              <TableCell align="center">
                <IconButton onClick={() => onQuantityChange(item._id, -1)}>
                  <Remove />
                </IconButton>
                {item.quantity}
                <IconButton onClick={() => onQuantityChange(item._id, 1)}>
                  <Add />
                </IconButton>
              </TableCell>
              <TableCell align="right">
                {formatPrice(item.price)}
              </TableCell>
              <TableCell align="right">
                {formatPrice(item.price * item.quantity)}
              </TableCell>
              <TableCell>
                <IconButton
                  color="error"
                  onClick={() => onRemove(item._id)}
                >
                  <Delete />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

/* ---------------- MAIN PAGE ---------------- */
const AddRestaurantBillPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const { menuItemsList, hotelProfile } = useSelector((state) => ({
    menuItemsList: state.menuItems?.menuItems?.list || [],
    hotelProfile: state.registration?.hotelProfile,
  }));

  const [menuLoading, setMenuLoading] = useState(true);

  const gstEnabled = Boolean(hotelProfile?.gst_enabled);

  const [customerName, setCustomerName] = useState("");
  const [customerMobile, setCustomerMobile] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const fetchMenu = async () => {
      setMenuLoading(true);
      dispatch(resetAllMenuItems());
      await dispatch(listAllMenuItems({ pageNumber: 1, pageSize: 200 }));
      setMenuLoading(false);
    };
    fetchMenu();
  }, [dispatch]);

  const categories = useMemo(() => {
    return ["All", ...new Set(menuItemsList.map((i) => i.category).filter(Boolean))];
  }, [menuItemsList]);

  const filteredMenu = useMemo(() => {
    return menuItemsList.filter(
      (i) =>
        i.status &&
        (selectedCategory === "All" || i.category === selectedCategory) &&
        i.item_name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [menuItemsList, selectedCategory, searchTerm]);

  const { subtotal, sgst, cgst, total } = useMemo(() => {
    const sub = selectedItems.reduce(
      (a, i) => a + i.price * i.quantity,
      0
    );

    if (!gstEnabled) {
      return { subtotal: sub, sgst: 0, cgst: 0, total: sub };
    }

    const s = sub * TAX_RATE;
    const c = sub * TAX_RATE;
    return { subtotal: sub, sgst: s, cgst: c, total: sub + s + c };
  }, [selectedItems, gstEnabled]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      customer_name: customerName,
      customer_mobile: customerMobile,
      items: selectedItems.map((i) => ({
        menu_id: i._id,
        item_name: i.item_name,
        quantity: i.quantity,
        rate: i.price,
        amount: i.price * i.quantity,
      })),
      amount: subtotal,
      sgst: gstEnabled ? sgst : 0,
      cgst: gstEnabled ? cgst : 0,
      total,
      pmt_status: "unpaid",
    };

    const id = await dispatch(addNewRestaurantInvoice(payload));
    navigate(`/billing/restaurants/${id}`);
  };

  return (
    <Box p={4} maxWidth={1200} mx="auto">
      <Typography variant="h5" mb={3}>
        Add Restaurant Invoice
      </Typography>

      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Customer Name"
              fullWidth
              required
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
            />
            <TextField
              label="Mobile"
              fullWidth
              value={customerMobile}
              onChange={(e) => setCustomerMobile(e.target.value)}
            />
          </Stack>

          <Divider />

          <Stack direction="row" spacing={2}>
            <TextField
              label="Search Menu"
              fullWidth
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <TextField
              select
              label="Category"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              {categories.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {cat}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <MenuGrid
            items={filteredMenu}
            isLoading={menuLoading}
            onAddItem={(item) =>
              setSelectedItems((p) => {
                const ex = p.find((i) => i._id === item._id);
                return ex
                  ? p.map((i) =>
                      i._id === item._id
                        ? { ...i, quantity: i.quantity + 1 }
                        : i
                    )
                  : [...p, { ...item, quantity: 1 }];
              })
            }
            onShowMore={() => setShowAll(true)}
            showAll={showAll}
            colors={colors}
          />

          <OrderSummary
            items={selectedItems}
            onQuantityChange={(id, d) =>
              setSelectedItems((p) =>
                p.map((i) =>
                  i._id === id
                    ? { ...i, quantity: Math.max(1, i.quantity + d) }
                    : i
                )
              )
            }
            onRemove={(id) =>
              setSelectedItems((p) => p.filter((i) => i._id !== id))
            }
          />

          {selectedItems.length > 0 && (
            <>
              <Box textAlign="right">
                <Typography>Subtotal: {formatPrice(subtotal)}</Typography>

                {gstEnabled && (
                  <>
                    <Typography>SGST: {formatPrice(sgst)}</Typography>
                    <Typography>CGST: {formatPrice(cgst)}</Typography>
                  </>
                )}

                <Typography variant="h6">
                  Total: {formatPrice(total)}
                </Typography>
              </Box>

            </>
          )}

          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
            >
              Back
            </Button>
            <Button type="submit" variant="contained" disabled={!selectedItems.length}>
              Submit Invoice
            </Button>
          </Stack>
        </Stack>
      </form>
    </Box>
  );
};

export default AddRestaurantBillPage;
